import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  collection, query, where, onSnapshot, doc, deleteDoc,
} from 'firebase/firestore';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  MailCheck, Search, Calendar, Download, Trash2, X,
} from 'lucide-react';

// Turn a Firestore Timestamp | ISO string | null into a JS Date (or null).
function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

const SOURCE_LABELS = {
  'shop-checkout': 'Shop checkout',
  'mailing-list-signup': 'Mailing list',
};

// RFC 4180 field escaping: wrap in quotes and double any embedded quotes.
function csvField(value) {
  const s = value == null ? '' : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

export default function AuthorSubscribersTab() {
  const { authorId } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmingId, setConfirmingId] = useState(null); // row awaiting delete confirmation
  const [deleting, setDeleting] = useState({});

  // Real-time listener for this author's subscribers.
  useEffect(() => {
    if (!authorId) return;
    const q = query(
      collection(db, 'authors', authorId, 'subscribers'),
      where('authorId', '==', authorId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const at = toDate(a.consentAt)?.getTime() || 0;
        const bt = toDate(b.consentAt)?.getTime() || 0;
        return bt - at; // most recent consent first
      });
      setSubscribers(data);
      setLoading(false);
    }, (err) => {
      console.error('Subscribers listener error:', err);
      setLoading(false);
    });
    return unsub;
  }, [authorId]);

  const filtered = useMemo(() => {
    if (!searchTerm) return subscribers;
    const term = searchTerm.toLowerCase();
    return subscribers.filter(s =>
      (s.email || '').toLowerCase().includes(term)
      || (s.firstName || '').toLowerCase().includes(term)
      || (s.lastName || '').toLowerCase().includes(term)
    );
  }, [subscribers, searchTerm]);

  const handleDelete = async (subscriber) => {
    if (deleting[subscriber.id]) return;
    setDeleting(prev => ({ ...prev, [subscriber.id]: true }));
    try {
      await deleteDoc(doc(db, 'authors', authorId, 'subscribers', subscriber.id));
      setConfirmingId(null);
    } catch (err) {
      console.error('Delete subscriber failed:', err);
      alert('Could not remove subscriber: ' + err.message);
    } finally {
      setDeleting(prev => ({ ...prev, [subscriber.id]: false }));
    }
  };

  const handleExportCsv = () => {
    const header = ['email', 'first name', 'last name', 'consent date', 'source'];
    const rows = filtered.map(s => {
      const d = toDate(s.consentAt);
      return [
        s.email || '',
        s.firstName || '',
        s.lastName || '',
        d ? d.toISOString() : '',
        s.source || '',
      ].map(csvField).join(',');
    });
    const csv = [header.map(csvField).join(','), ...rows].join('\r\n');
    // Prepend a BOM so Excel opens UTF-8 (e.g. accented names) correctly.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
          <p className="text-sm text-slate-500">Loading subscribers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Subscribers</h2>
          <p className="text-sm text-slate-500">
            Readers who agreed to hear from you at checkout. One entry per email.
          </p>
        </div>
        <Button
          onClick={handleExportCsv}
          disabled={filtered.length === 0}
          className="gap-2 bg-gradient-to-r from-slate-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Stat */}
      <div className="grid grid-cols-1 gap-4 sm:max-w-xs">
        <Card className="rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="flex items-center gap-3 px-4 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 text-teal-600">
              <MailCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">{subscribers.length}</p>
              <p className="text-xs text-slate-500">Total subscribers</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <Card className="rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="py-12 text-center">
            <MailCheck className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              {subscribers.length === 0 ? 'No subscribers yet' : 'No subscribers match your search'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50/60 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Consent date</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(sub => {
                  const d = toDate(sub.consentAt);
                  const fullName = [sub.firstName, sub.lastName].filter(Boolean).join(' ');
                  const isConfirming = confirmingId === sub.id;
                  return (
                    <tr key={sub.id} className="hover:bg-slate-50/60">
                      <td className="px-5 py-3 font-medium text-slate-900">{fullName || '—'}</td>
                      <td className="px-5 py-3 text-slate-600">{sub.email}</td>
                      <td className="px-5 py-3 text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          {d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                          {SOURCE_LABELS[sub.source] || sub.source || '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        {isConfirming ? (
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-xs text-slate-500">Remove?</span>
                            <Button
                              size="sm"
                              onClick={() => handleDelete(sub)}
                              disabled={deleting[sub.id]}
                              className="bg-red-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-700"
                            >
                              {deleting[sub.id] ? 'Removing...' : 'Confirm'}
                            </Button>
                            <button
                              onClick={() => setConfirmingId(null)}
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                              aria-label="Cancel"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmingId(sub.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <p className="text-xs text-slate-400">
        Removing a subscriber deletes their record permanently — use this for unsubscribe or
        data-erasure requests. It does not affect the reader's account or the Mailing List tab.
      </p>
    </div>
  );
}
